/**
 * Content for an alert that allows a teacher to enter a 4-digit pin to
 * override something in the app (e.g. a student being locked into a task).
 * Currently, the correct pin is the Monday of the current week as "DDMM".
 */
import React from 'react';
import PropTypes from 'prop-types';

import { getMondayBeforeCode } from 'phoenix2/utils/date';
import { KeypadNumberInput } from 'phoenix2/components/widgets/keypad-number-input';

import alertUpdate from 'imported-assets/img/alert_update.png';

const INCORRECT_CODE_MESSAGE = 'Invalid code';

class TeacherOverrideAlert extends React.Component {
    constructor(props) {
        super(props);

        // Calculate the correct code
        this._correctCode = getMondayBeforeCode();

        this.state = {
            code: '',
            validationMessage: '',
        };
    }

    static displayName = 'TeacherOverrideAlert';

    /**
     * onEndAlert - function to be called by this component to close the alert
     */
    static propTypes = {
        onEndAlert: PropTypes.func.isRequired,
    };

    render() {
        const { onEndAlert } = this.props;

        const enableEnterButton = this.state.code.length === 4;
        const enterButtonClassName = 'btn' + (enableEnterButton ? '' : ' btn-disabled');

        return (
            <div className="teacher-override-alert">
                <div className="alert-message">Enter the 4-digit override code</div>
                <KeypadNumberInput
                    minCharacters={4}
                    maxCharacters={4}
                    allowDecimal={false}
                    allowNegative={false}
                    allowEnter={false}
                    enableAnimation={false}
                    onChange={this.onCodeChanged.bind(this)}
                    onSubmit={this.onSubmit.bind(this)}
                    gridKeypadMinHeight={700}
                    hideDigits={true}
                />
                <div className="validation-message">
                    {this.state.validationMessage && [
                        <img key="validation-image" src={alertUpdate} />,
                        <span key="validation-message">{this.state.validationMessage}</span>,
                    ]}
                </div>
                <div className="alert-buttons">
                    <div
                        tabIndex="0"
                        key="cancel"
                        className="btn btn-secondary"
                        onClick={() => onEndAlert('back')}
                        onMouseDown={evt => {
                            // Prevent focussing when clicked:
                            evt.preventDefault();
                        }}
                    >
                        Cancel
                    </div>
                    <div
                        tabIndex="0"
                        key="enter"
                        className={enterButtonClassName}
                        disabled={!enableEnterButton}
                        onClick={this.onSubmit.bind(this)}
                        onMouseDown={evt => {
                            // Prevent focussing when clicked:
                            evt.preventDefault();
                        }}
                    >
                        Enter
                    </div>
                </div>
            </div>
        );
    }

    onCodeChanged(newCode) {
        let validationMessage = this.state.validationMessage;
        if (this.state.code !== newCode) {
            if (newCode === this._correctCode || newCode.length === 0) {
                validationMessage = '';
            }
            this.setState({ code: newCode, validationMessage });
        }
    }

    /**
     * Pressing keyboard enter handled by keypad will submit the code and
     * close the alert, if it is valid
     */
    onSubmit() {
        if (this.codeValid()) {
            this.props.onEndAlert('overridden');
        } else if (this.state.validationMessage !== INCORRECT_CODE_MESSAGE) {
            this.setState({
                validationMessage: INCORRECT_CODE_MESSAGE,
            });
        }
    }

    /**
     * Determine if the currently entered code is valid
     */
    codeValid() {
        return this.state.code === this._correctCode;
    }
}

export { TeacherOverrideAlert };
